/**
 * FamilySelect — Selector maestro de Familias (V1.42)
 * ====================================================
 * Alimenta el campo "Familia" en catálogos maestros (Proveedores,
 * Métodos de pago, Tipos de Activos) desde la colección `catalog_families`.
 *
 * Norma corporativa V1.41/V1.42:
 *   ENTIDAD/AGRUPACIÓN REUTILIZABLE = SELECTOR desde catálogo maestro
 *   OPERACIÓN = TEXTO LIBRE (descripción, notas)
 *
 * Mismo patrón que <ProviderSelect>:
 * - Autocompletado con normalización acentos/mayúsculas
 * - Anti-duplicados en el selector
 * - Botón "+ Crear nueva familia" inline
 * - Familias inactivas NO son seleccionables
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, Plus, Search, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const norm = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/\s+/g, " ")
  .trim();

const _cache = {};   // { kind: [families] }

async function fetchFamilies(kind, force = false) {
  // V1.43: FamilySelect consume /api/categories con kind maestro (X_group)
  const masterKind = kind.endsWith("_group") ? kind : `${kind}_group`;
  if (!force && _cache[masterKind]) return _cache[masterKind];
  const r = await api.get("/categories", { params: { kind: masterKind } });
  _cache[masterKind] = (r.data || []).map((c) => ({ ...c, usage_count: c.usage_count ?? 0 }));
  return _cache[masterKind];
}

export function invalidateFamilyCache(kind) {
  if (kind) {
    const masterKind = kind.endsWith("_group") ? kind : `${kind}_group`;
    delete _cache[masterKind];
  } else Object.keys(_cache).forEach((k) => delete _cache[k]);
}

export default function FamilySelect({
  kind,                     // "provider" | "payment_method" | "asset_type"
  value,                    // family_id_ref actual o null
  onChange,                 // (family_id, family_obj) => void
  legacyGroupName = "",     // valor histórico si el registro tenía group como texto libre
  placeholder = "Selecciona una familia…",
  disabled = false,
  testId = "family-select",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => { if (kind) fetchFamilies(kind).then(setItems).catch(() => {}); }, [kind]);

  const selected = useMemo(() => items.find((f) => f.id === value) || null, [items, value]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const arr = items.filter((f) => f.active !== false);
    if (!q) return arr;
    return arr.filter((f) => norm(f.name).includes(q));
  }, [items, query]);

  const exactMatch = useMemo(() => {
    if (!query.trim()) return null;
    const q = norm(query);
    return items.find((f) => norm(f.name) === q) || null;
  }, [items, query]);

  const doCreate = async () => {
    const name = (newName || query).trim();
    if (!name) return;
    // Anti-duplicado — reutilizar el existente sin lanzar error
    const dup = items.find((f) => norm(f.name) === norm(name));
    if (dup) {
      onChange(dup.id, dup);
      setOpen(false); setCreating(false); setQuery(""); setNewName("");
      toast.info(`"${dup.name}" ya existe — se ha seleccionado.`);
      return;
    }
    try {
      const masterKind = kind.endsWith("_group") ? kind : `${kind}_group`;
      const r = await api.post("/categories", { kind: masterKind, name, parent_id: null, active: true });
      invalidateFamilyCache(kind);
      const list = await fetchFamilies(kind, true);
      setItems(list);
      onChange(r.data.id, r.data);
      setOpen(false); setCreating(false); setQuery(""); setNewName("");
      toast.success(`Familia "${r.data.name}" creada`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const displayLabel = selected?.name
    || (legacyGroupName ? `${legacyGroupName} · (histórico — vincular)` : "");

  return (
    <div className="relative" data-testid={testId}>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setCreating(false); setQuery(""); }}}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testId}-trigger`}
            className={`w-full flex items-center justify-between gap-2 h-10 px-3 py-2 rounded-md border border-input bg-background text-sm text-left hover:border-foreground/40 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <span className="flex items-center gap-2 min-w-0 flex-1">
              <Layers size={14} className="text-muted-foreground shrink-0" />
              <span className={`truncate ${!selected && !legacyGroupName ? "text-muted-foreground" : ""} ${legacyGroupName && !selected ? "text-amber-700 italic" : ""}`}>
                {displayLabel || placeholder}
              </span>
            </span>
            <ChevronsUpDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-md" align="start">
          {!creating ? (
            <div>
              <div className="relative border-b border-border">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  autoFocus
                  data-testid={`${testId}-search`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar familia…"
                  className="w-full h-10 pl-9 pr-3 text-sm bg-transparent focus:outline-none"
                />
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    Sin resultados{query.trim() ? ` para "${query}"` : ""}.
                  </div>
                ) : (
                  filtered.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      data-testid={`${testId}-option-${f.id}`}
                      onClick={() => { onChange(f.id, f); setOpen(false); setQuery(""); }}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-secondary/60 text-left"
                    >
                      <div className="min-w-0">
                        <span className="truncate">{f.name}</span>
                        {f.usage_count > 0 && <span className="ml-2 text-[10px] text-muted-foreground">({f.usage_count})</span>}
                      </div>
                      {value === f.id && <Check size={14} className="text-primary shrink-0" />}
                    </button>
                  ))
                )}
              </div>
              <div className="border-t border-border p-2 bg-secondary/30 flex gap-2">
                {value && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => { onChange(null, null); setOpen(false); }}>Sin familia</Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1 justify-start"
                  data-testid={`${testId}-create-btn`}
                  onClick={() => { setCreating(true); setNewName(query.trim()); }}
                  disabled={!!exactMatch}
                >
                  <Plus size={14} className="mr-1" />
                  {exactMatch ? `Ya existe: ${exactMatch.name}` : (query.trim() ? `Crear "${query.trim()}"` : "Crear nueva familia")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Nueva familia</div>
              <Input
                autoFocus
                data-testid={`${testId}-new-name`}
                placeholder="Nombre (ej. Bancos, Tarjetas, Administración Pública…)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doCreate()}
              />
              <div className="text-[11px] text-muted-foreground">
                Antes de crear, comprueba si ya existe con otro nombre parecido. Las familias son compartidas por toda la familia radar.
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" className="flex-1" onClick={() => setCreating(false)}>Cancelar</Button>
                <Button type="button" size="sm" className="flex-1" onClick={doCreate} disabled={!newName.trim()} data-testid={`${testId}-create-confirm`}>Crear y seleccionar</Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
